import React from "react";
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Fade,
  IconButton,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import BuildIcon from "@mui/icons-material/Build";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import WarningIcon from "@mui/icons-material/Warning";
import InfoIcon from "@mui/icons-material/Info";
import CampaignIcon from "@mui/icons-material/Campaign";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { NewsCategory, NewsItem } from "../shared/interfaces/NewsItem";
import { formatNewsTimestamp } from "../shared/utils/newsFilters";

interface NewsModalProps {
  open: boolean;
  onClose: () => void;
  newsItems: NewsItem[];
}

const categoryIcons: Record<NewsCategory, typeof InfoIcon> = {
  MAINTENANCE: BuildIcon,
  FEATURE: NewReleasesIcon,
  ALERT: WarningIcon,
  INFO: InfoIcon,
};

const categoryColors: Record<NewsCategory, string> = {
  MAINTENANCE: "#8b5cf6",
  FEATURE: "#10b981",
  ALERT: "#f59e0b",
  INFO: "#6366f1",
};

const categoryLabels: Record<NewsCategory, string> = {
  MAINTENANCE: "Maintenance",
  FEATURE: "New Feature",
  ALERT: "Alert",
  INFO: "Update",
};

export const NewsModal: React.FC<NewsModalProps> = ({
  open,
  onClose,
  newsItems,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="news-modal-title"
      aria-describedby="news-modal-description"
      TransitionComponent={Fade}
      transitionDuration={200}
      PaperProps={{
        elevation: 0,
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "divider",
        },
      }}
      sx={{
        "& .MuiDialog-container": {
          alignItems: "center",
        },
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          },
        },
      }}
    >
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          position: "absolute",
          right: 12,
          top: 12,
          color: "text.secondary",
          zIndex: 1,
          "&:hover": {
            backgroundColor: "action.hover",
          },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      <DialogContent sx={{ pt: 4, pb: 2, px: { xs: 2.5, sm: 4 } }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 3 }}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "50%",
              backgroundColor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto 16px",
              boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.35)}`,
            }}
          >
            <CampaignIcon sx={{ fontSize: 26, color: "white" }} />
          </Box>

          <Typography
            id="news-modal-title"
            variant="h5"
            component="h2"
            sx={{ fontWeight: 700, mb: 0.5, color: "text.primary" }}
          >
            What's New
          </Typography>

          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {newsItems.length === 0
              ? "No new updates"
              : `${newsItems.length} update${
                  newsItems.length === 1 ? "" : "s"
                } for you`}
          </Typography>
        </Box>

        {newsItems.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 4,
              px: 2,
              backgroundColor: alpha(theme.palette.success.main, 0.08),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
            }}
          >
            <CheckCircleOutlineIcon
              sx={{
                fontSize: 48,
                color: "success.main",
                mb: 1.5,
              }}
            />
            <Typography
              variant="body1"
              sx={{ fontWeight: 500, color: "text.primary", mb: 0.5 }}
            >
              You're all caught up!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Check back later for new updates and announcements.
            </Typography>
          </Box>
        ) : (
          <Stack
            spacing={2}
            sx={{
              maxHeight: 500,
              overflowY: "auto",
              pr: 0.5,
              "&::-webkit-scrollbar": {
                width: 5,
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "transparent",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: alpha(theme.palette.text.primary, 0.15),
                borderRadius: 3,
              },
            }}
          >
            {newsItems.map((item) => {
              const categoryColor =
                categoryColors[item.category] || theme.palette.primary.main;
              const IconComponent = categoryIcons[item.category] || InfoIcon;
              return (
                <Paper
                  key={item.newsId}
                  elevation={0}
                  sx={{
                    p: 2.5,
                    backgroundColor: alpha(theme.palette.grey[500], 0.04),
                    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                    borderRadius: 2.5,
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      backgroundColor: alpha(categoryColor, 0.06),
                      borderColor: alpha(categoryColor, 0.3),
                    },
                  }}
                >
                  {/* Category badge + timestamp row */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      mb: 1.5,
                    }}
                  >
                    <Chip
                      icon={<IconComponent sx={{ fontSize: 14 }} />}
                      label={categoryLabels[item.category] || "Update"}
                      size="small"
                      sx={{
                        height: 26,
                        fontSize: "0.72rem",
                        fontWeight: 600,
                        backgroundColor: alpha(categoryColor, 0.12),
                        color: categoryColor,
                        borderRadius: 1.5,
                        "& .MuiChip-icon": {
                          color: categoryColor,
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        fontSize: "0.7rem",
                      }}
                    >
                      {formatNewsTimestamp(item.createdAt)}
                    </Typography>
                  </Box>

                  {/* Title */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      wordBreak: "break-word",
                      color: "text.primary",
                      lineHeight: 1.4,
                      mb: 1,
                    }}
                  >
                    {item.title}
                  </Typography>

                  {/* Content */}
                  <Box
                    sx={{
                      color: "text.secondary",
                      fontSize: "0.875rem",
                      lineHeight: 1.7,
                      "& a": {
                        color: "primary.main",
                        textDecoration: "none",
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      },
                      "& p": {
                        marginBottom: 1,
                      },
                      "& ul, & ol": {
                        paddingLeft: 2.5,
                        marginTop: 0.5,
                        marginBottom: 1,
                      },
                      "& li": {
                        marginBottom: 0.5,
                      },
                      "& img": {
                        maxWidth: "100%",
                        height: "auto",
                        borderRadius: 1,
                        marginTop: 1,
                        marginBottom: 1,
                      },
                      "& table": {
                        width: "100%",
                        borderCollapse: "collapse",
                        "& td, & th": {
                          padding: "6px 12px",
                          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
                          fontSize: "0.825rem",
                        },
                      },
                      "& > *:first-of-type": {
                        marginTop: 0,
                      },
                      "& > *:last-child": {
                        marginBottom: 0,
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: item.content }}
                  />
                </Paper>
              );
            })}
          </Stack>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: 4,
          pb: 3,
          pt: 2,
          justifyContent: "center",
        }}
      >
        <Button
          onClick={onClose}
          variant="contained"
          disableElevation
          sx={{
            py: 1,
            px: 5,
            borderRadius: 2,
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 600,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: "white",
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              background: `linear-gradient(135deg, ${
                theme.palette.primary.dark
              } 0%, ${alpha(theme.palette.primary.dark, 0.9)} 100%)`,
              transform: "translateY(-1px)",
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.4)}`,
            },
            "&:active": {
              transform: "translateY(0)",
            },
          }}
        >
          Got It
        </Button>
      </DialogActions>
    </Dialog>
  );
};
